import type { LineNotesClient } from '@/backend/contracts';
import { getBackend } from '@/backend';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import type { LineNote, LineNoteDraft } from '../types/line-notes';
import type { ProjectScope } from '../../sync/project-scope';
import { enqueueStructuredEntityWrite, type StructuredEntityWriteRegistration } from '../../sync/structured-entity-write';

const LINE_NOTE_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'notes_reports',
    capability: 'EDIT_NOTES',
    conflictPrefix: 'lineNotes',
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: 'Authentication required to preserve line note project writes',
};

function requireLineNoteActorId(): string {
    const actorId = getEffectiveUserId();
    if (!actorId) {
        throw new Error('Authentication required to preserve line note project writes');
    }
    return actorId;
}

function requireLineNotesClient(): LineNotesClient {
    const client = getBackend().lineNotes;
    if (!client) {
        throw new Error('Line notes backend client is not configured.');
    }
    return client;
}

export const LineNotesApi = {
    async createLineNote(scope: ProjectScope, note: LineNoteDraft): Promise<LineNote> {
        const actorId = requireLineNoteActorId();
        const newNote: LineNote = {
            ...note,
            id: crypto.randomUUID(),
            createdBy: actorId,
        };
        await enqueueStructuredEntityWrite({
            scope,
            actorId,
            registration: LINE_NOTE_WRITE_REGISTRATION,
            entityId: newNote.id,
            kind: 'patchEntity',
            payload: newNote,
        });
        return newNote;
    },

    async getLineNotes(scope: ProjectScope): Promise<LineNote[]> {
        return requireLineNotesClient().loadProjectLineNotes(scope);
    },

    async getLineNotesByDate(scope: ProjectScope, date: string): Promise<LineNote[]> {
        return requireLineNotesClient().loadLineNotesByDate(scope, date);
    },

    async deleteLineNote(scope: ProjectScope, noteId: string): Promise<void> {
        await enqueueStructuredEntityWrite({
            scope,
            registration: LINE_NOTE_WRITE_REGISTRATION,
            entityId: noteId,
            kind: 'deleteEntity',
            payload: { id: noteId },
        });
    },

    async updateLineNote(
        scope: ProjectScope,
        noteId: string,
        updates: Pick<LineNote, 'comment'>
    ): Promise<void> {
        await enqueueStructuredEntityWrite({
            scope,
            registration: LINE_NOTE_WRITE_REGISTRATION,
            entityId: noteId,
            kind: 'patchEntity',
            payload: { id: noteId, ...updates },
        });
    },

    /**
     * Get all line notes for a specific script block (useful for heatmap/history)
     */
    async getLineNotesForBlock(scope: ProjectScope, scriptBlockId: string): Promise<LineNote[]> {
        const notes = await requireLineNotesClient().loadProjectLineNotes(scope);
        return notes.filter(note => note.scriptBlockId === scriptBlockId);
    }
};
